import { createApi } from "@reduxjs/toolkit/query/react";
import { publicBaseQuery } from "../base/public/publicBaseQuery";
import type {
  AssignmentReportInfoResponse,
  GetAssignmentReportInfoRequest,
} from "@/types/endpoints/reports/GetAssignmentReportInfo";
import type {
  AssignmentReportDataResponse,
  GetAssignmentReportDataRequest,
} from "@/types/endpoints/reports/GetAssignmentReportData";
import type {
  PublicAiReportsResponse,
  GetPublicAiReportsRequest,
} from "@/types/endpoints/reports/GetPublicAiReports";
import type {
  GetAssignmentReportResultsRequest,
  GetAssignmentReportResultsResponse,
} from "@/types/endpoints/reports/GetAssignmentReportResults";

/**
 * Public Report API
 * Used for public access to assignment reports via token-based authentication
 */
export const publicReportApi = createApi({
  reducerPath: "publicReportApi",
  baseQuery: publicBaseQuery(),
  tagTypes: ["ReportInfo", "ReportData"],
  keepUnusedDataFor: 300, // 5 minutes cache
  endpoints: (builder) => ({
    /**
     * Get basic report information (assignment, student, certificate)
     * Endpoint: GET /api/assignment-report/${token}/info.json
     */
    getAssignmentReportInfo: builder.query<
      AssignmentReportInfoResponse,
      GetAssignmentReportInfoRequest
    >({
      query: ({ token }) => ({
        url: `/api/assignment-report/${token}/info.json`,
        method: "GET",
      }),
      providesTags: (result, error, { token }) => [
        { type: "ReportInfo", id: token },
      ],
    }),

    /**
     * Get detailed report data (attempts, violations, identities, actions, results, etc.)
     * Endpoint: GET /api/assignment-report/${token}/assignment.json
     */
    getAssignmentReportData: builder.query<
      AssignmentReportDataResponse,
      GetAssignmentReportDataRequest
    >({
      query: ({ token, attemptId }) => {
        // Build fields list based on what we need
        const fields = [
          "attempts",
          "actions",
          "violations",
          "identities",
          "webinar_sessions",
          "webinar_records",
          "credibility",
          "results_chart",
          "is_started",
          "is_finished",
          "progress",
          "quiz_components",
          "results",
          "scores",
          "student_assessments",
        ];

        return {
          url: `/api/assignment-report/${token}/assignment.json`,
          method: "GET",
          params: attemptId ? { assignment_attempt_id: attemptId } : undefined,
          headers: {
            "X-Requested-Fields": fields.join(","),
          },
        };
      },
      providesTags: (result, error, { token, attemptId }) => [
        { type: "ReportData", id: `${token}-${attemptId || "default"}` },
      ],
    }),

    /**
     * Get results for a specific attempt in public report
     * Endpoint: GET /api/assignment-report/${token}/results.json
     * Note: Using high limit (999) to fetch all results in one request
     */
    getAssignmentReportResults: builder.query<
      GetAssignmentReportResultsResponse,
      GetAssignmentReportResultsRequest
    >({
      query: ({ token, attemptId, limit = 999 }) => ({
        url: `/api/assignment-report/${token}/results.json`,
        method: "GET",
        params: {
          assignment_attempt_id: attemptId,
          limit,
        },
      }),
      providesTags: (_result, _error, { token, attemptId }) => [
        { type: "ReportData", id: `results-${token}-${attemptId}` },
      ],
    }),

    getPublicAiReports: builder.query<
      PublicAiReportsResponse,
      GetPublicAiReportsRequest
    >({
      async queryFn({ attemptId, reportTypes, forceRegenerate = false }, _api, _extra) {
        try {
          // const baseUrl = process.env.NEXT_PUBLIC_API_RATING_URL;
          const baseUrl = `${process.env.NEXT_PUBLIC_API_RATING_URL}reports/ai/`;
          const url = new URL(`public/${attemptId}`, baseUrl);
          
          if (reportTypes && reportTypes.length > 0) {
            reportTypes.forEach(type => {
              url.searchParams.append('report_types', type);
            });
          }

          url.searchParams.append('force_regenerate', String(forceRegenerate));

          const response = await fetch(url.toString(), {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          });

          if (!response.ok) {
            return {
              error: {
                status: response.status,
                data: await response.text(),
              },
            };
          }

          const data: PublicAiReportsResponse = await response.json();
          return { data };
        } catch (error) {
          return {
            error: {
              status: 'FETCH_ERROR',
              data: error instanceof Error ? error.message : 'Unknown error',
            },
          };
        }
      },
      providesTags: (result, error, { attemptId }) =>
        result
          ? [{ type: "ReportData", id: `ai-public-${attemptId}` }]
          : ["ReportData"],
    }),
  }),
});

export const {
  useGetAssignmentReportInfoQuery,
  useGetAssignmentReportDataQuery,
  useGetAssignmentReportResultsQuery,
  useGetPublicAiReportsQuery,
} = publicReportApi;
